import './Dashboard.css';
import { FaHome, FaChartBar, FaQuestionCircle } from 'react-icons/fa';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TaxYearView from './TaxYearView'
import FeatureRequest from './FeatureRequest';
import BugReport from './BugReport';
import { Line, Bar } from 'react-chartjs-2';
import api from '../axiosConfig';
import ActionsWidget from './ActionsWidget'; 
import {
    formatCurrency,
    formatCurrencyDecimal,
    formatCurrencyDollars
} from './utils/formatting'; 
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';



ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Tooltip,
    Legend,
    Filler
);

function Dashboard() {
    const [data, setData] = useState([]);
    const [availableYears, setAvailableYears] = useState([]);
    const [firstName, setFirstName] = useState(''); // State to store user's first name
    const [currentView, setCurrentView] = useState('Dashboard');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('token');
            try {
                const response = await api.get('/userdata', {
                    headers: { Authorization: `Bearer ${token}` },
                });


                const fetchedData = Array.isArray(response.data) ? response.data : [response.data];             


                setData(fetchedData);

                // Extract the first name from the first data entry
                if (fetchedData.length > 0) {
                    setFirstName(fetchedData[0].firstName || '');
                }

                const years = fetchedData.map(item => item.taxYear);
                const sortedYears = [...new Set(years)].sort((a, b) => b - a);
                setAvailableYears(sortedYears);
            } catch (error) {
                console.error('Error fetching user data:', error);
                alert('Failed to fetch data. Please log in again.');
            }
        };
        fetchData();
    }, []);


    // Handlers
    const handleNavigation = (view) => {
        setCurrentView(view);
    };

    const handleSignOut = () => {
        localStorage.removeItem('token');
        setCurrentView('Dashboard'); // Reset view
        navigate('/');
    };

    
    const agiDataset = data.map(item => ({
        year: item.taxYear,
        agi: item.financialMetrics.agi,
        taxableIncome: item.financialMetrics.taxableIncome,
        totalTax: item.financialMetrics.totalTax,
        returnAmount: item.financialMetrics.returnAmt,
        netStateReturn: item.financialMetrics.netStateReturn,
    })).sort((a, b) => a.year - b.year);


    const mostRecentYearData = agiDataset[agiDataset.length - 1];
    const mostRecentReturn_federal = mostRecentYearData?.returnAmount || 0;
    const mostRecentReturn_state = mostRecentYearData?.netStateReturn || 0;
   


    // Adjusted Gross Income Chart Data
    const agiChartData = {
        labels: agiDataset.map(item => item.year),
        datasets: [
            {
                label: 'AGI',
                data: agiDataset.map(item => item.agi),
                borderColor: '#6a84a6',
                backgroundColor: 'rgba(106, 132, 166, 0.2)',
                fill: true,
                tension: 0.4,
                pointRadius: 5,
            },
            {
                label: 'Taxable Income',
                data: agiDataset.map(item => item.taxableIncome),
                borderColor: '#999',
                backgroundColor: 'rgba(153, 153, 153, 0.2)',
                fill: false,
                tension: 0.4,
                pointRadius: 5,
            }
        ]
    };

    // Federal Tax Liability Chart Data
    const taxChartData = {
        labels: agiDataset.map(item => item.year),
        datasets: [
            {
                data: agiDataset.map(item => item.totalTax),
                backgroundColor: 'rgba(69, 69, 69, 0.8)',
                borderColor: '#454545',
                borderWidth: 1,
            },
        ],
    };

  
    /* Define financial summary measures */
    const totalAgi = agiDataset.reduce((sum, item) => sum + (item.agi || 0), 0);
    const totalTaxes = agiDataset.reduce((sum, item) => sum + (item.totalTax || 0), 0);
    const totalTaxableIncome = agiDataset.reduce((sum, item) => sum + (item.taxableIncome || 0), 0);
    const averageTaxes = agiDataset.length > 0 ? (totalTaxes / agiDataset.length) : 0;
   

 


    // Common Scales for below charts
    const commonAxesStyles_x = {
        grid: { display:false },
        ticks: {
            color: '#6a6a6a', // Light gray
            font: { weight: 'bold' },
        },
    };

    const commonAxesStyles_y = {
        grid: { color: '#ddd' },
        ticks: {
            color: '#6a6a6a', // Light gray
            font: { weight: 'bold' },
            callback:(value) => formatCurrency(value),
        },
    };


    return (
        <div className="dashboard-container">


            {/* Sidebar */}
            <div className="sidebar">
          
                {/* Welcome message */}
                {firstName && <div className="greeting"> <b> Hi, {firstName}</b></div>}


                {/* Navigation Buttons */}
                <button
                    onClick={() => handleNavigation('Dashboard')}
                    className={currentView === 'Dashboard' ? 'active' : ''}
                >
                    <FaHome className="icon" />
                    Dashboard
                </button>
                {availableYears.map((year) => (
                    <button
                        key={year}
                        onClick={() => handleNavigation(year)}
                        className={currentView === year ? 'active' : ''}
                    >
                        <FaChartBar className="icon" />
                        {year}
                    </button>
                ))}


                <div className="logo-container">
                    <img src={`${process.env.PUBLIC_URL}/salvalogos/salva_logo_black_transparent.png`} alt="Salva Logo" />
                </div>
            </div>


            {/*Dashboard Widgets*/ }
            <div className="main-content">
                {currentView === 'Dashboard' && (
                    <>
                        {/* Action Widget */}
                        <ActionsWidget
                            onFeatureRequest={() => handleNavigation('FeatureRequest')}
                            onBugReport={() => handleNavigation('BugReport')}
                            onSignOut={handleSignOut}
                        />



                        {/*Adjusted Gross Income Widget*/}
                    <div className="widget">
                        <div className="widget-header">Adjusted Gross Income</div>
                        <div className="widget-content">
                            <div style={{ flex: 2 }}>
                                <Line
                                    data={agiChartData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            tooltip: {
                                                callbacks: {
                                                    title: (tooltipItems) => `Tax Year: ${tooltipItems[0].label}`,
                                                    label: (tooltipItem) => `${tooltipItem.dataset.label}: $${tooltipItem.raw.toLocaleString()}`,
                                                },
                                            },
                                        },
                                        scales: {
                                            x: commonAxesStyles_x,
                                            y: commonAxesStyles_y,
                                        },
                                    }}
                                />
                            </div>
                            <div className="widget-text">
                                Since <b style={{ color: '#6a84a6' }}>{agiDataset[0]?.year}</b> you've earned a total of <b style={{ color: '#6a84a6' }}>{formatCurrency(totalAgi)}</b>!
                                <br />
                                <br />
                                Even better: you were only taxed on <b style={{ color: '#6a84a6' }}> {formatCurrency(totalTaxableIncome)}</b> of these earnings!
                            </div>
                        </div>
                    </div>

                        {/*Federal Tax Liability Widget*/}
                    <div className="widget">
                            <div className="widget-header">
                                Federal Tax{' '}
                                <div className="tooltip-container">
                                    <FaQuestionCircle className="tooltip-icon" />
                                    <span className="tooltip-text2">
                                        This is how much tax the IRS expects you to pay based on what you earned.</span>
                                </div>
                            </div>
                        <div className="widget-content">
                            <div style={{ flex: 2 }}>
                                <Bar
                                    data={taxChartData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        borderRadius: 10,
                                        plugins: {
                                            tooltip: {
                                                callbacks: {
                                                    title: (tooltipItems) => `Tax Year: ${tooltipItems[0].label}`,
                                                    label: (tooltipItem) => `${tooltipItem.label}: ${formatCurrencyDollars(tooltipItem.raw)}`,
                                                },
                                            },
                                            legend: {
                                                display: false,
                                            },
                                        },
                                        scales: {
                                            x: commonAxesStyles_x,
                                            y: commonAxesStyles_y,
                                        },
                                    }}
                                />
                            </div>
                            <div className="widget-text">
                                    You've paid a total of <b style={{ color: '#6a84a6' }}>{formatCurrencyDecimal(totalTaxes)}</b> in federal taxes since then
                                    (that's about <b style={{ color: '#6a84a6' }}>{formatCurrencyDecimal(averageTaxes)}</b> each year).
                            </div>
                        </div>
                    </div>

                        {/*Federal Tax Return Widget*/}
                        <div className="widget">
                            <div className="widget-header">
                                Federal Tax Return{' '}
                                <div className="tooltip-container">
                                    <FaQuestionCircle className="tooltip-icon" />
                                    <span className="tooltip-text2">
                                        This is how close you were to satisfying your federal tax liability for the year.
                                        <br />
                                        <br />
                                        If you made a payment, you didn't give the IRS enough.
                                        <br />
                                        <br/>
                                        If you got a refund, you gave them too much!
                                    </span>
                                </div>
                            </div>
                            <div className="widget-content">
                                <div style={{ flex: 2, position: 'relative' }}>
                                    {/* Bar and Line Chart */}
                                    <Bar
                                        data={{

                                            labels: agiDataset.map(item => item.year),
                                            datasets: [
                                                {
                                                    type: 'bar',
                                                    label: 'Federal Return Amount',
                                                    data: agiDataset.map(item => item.returnAmount),
                                                    backgroundColor: agiDataset.map(item =>
                                                        item.returnAmount >= 0 ? 'rgba(0, 125, 0, 0.6)' : 'rgba(221, 0, 0, 0.6)'
                                                    ),
                                                    borderColor: '#000',
                                                    borderWidth: 1,
                                                    barPercentage: 0.8,
                                                    categoryPercentage: 0.6,
                                                    borderRadius: 10,
                                                },
                                                {
                                                    type: 'line',
                                                    label: 'Trend',
                                                    data: agiDataset.map(item => item.returnAmount),
                                                    borderColor: '#6a84a6',
                                                    borderWidth: 2,
                                                    pointBackgroundColor: '#6a84a6',
                                                    tension: 0.4,
                                                    fill: false,
                                                },
                                            ],
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                tooltip: {
                                                    callbacks: {
                                                        title: (tooltipItems) => `Tax Year: ${tooltipItems[0].label}`,
                                                        label: (tooltipItem) =>
                                                            `${tooltipItem.dataset.label}: ${formatCurrencyDollars(tooltipItem.raw)}`,
                                                    },
                                                },
                                                legend: {
                                                    position: 'top',
                                                },
                                            },
                                            scales: {
                                                x: commonAxesStyles_x,
                                                y: commonAxesStyles_y,
                                            },
                                        }}
                                    />
                                </div>
                                <div className="widget-text">
                                    Last year you {mostRecentReturn_federal >= 0 ? 'got a refund from the IRS' : 'made a payment to the IRS'} of{' '}
                                    <b style={{ color: '#6a84a6' }}>{formatCurrencyDecimal(mostRecentReturn_federal)}</b>.
                                </div>
                            </div>
                        </div>


                        {/*State Tax Return Widget*/}
                        <div className="widget">
                            <div className="widget-header">State Tax Return</div>
                            <div className="widget-content">
                                <div style={{ flex: 2, position: 'relative' }}>
                                    {/* Bar and Line Chart */}
                                    <Bar
                                        data={{
                                            labels: agiDataset.map(item => item.year),
                                            datasets: [
                                                {
                                                    type: 'bar',
                                                    label: 'State Return Amount',
                                                    data: agiDataset.map(item => item.netStateReturn),
                                                    backgroundColor: agiDataset.map(item =>
                                                        item.netStateReturn >= 0 ? 'rgba(0, 125, 0, 0.6)' : 'rgba(221, 0, 0, 0.6)'
                                                    ),
                                                    borderColor: '#000',
                                                    borderWidth: 1,
                                                    barPercentage: 0.8,
                                                    categoryPercentage: 0.6,
                                                    borderRadius: 10,
                                                },
                                                {
                                                    type: 'line',
                                                    label: 'Trend',
                                                    data: agiDataset.map(item => item.netStateReturn),
                                                    borderColor: '#6a84a6',
                                                    borderWidth: 2,
                                                    pointBackgroundColor: '#6a84a6',
                                                    tension: 0.4,
                                                    fill: false,
                                                },
                                            ],
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                tooltip: {
                                                    callbacks: {
                                                        title: (tooltipItems) => `Tax Year: ${tooltipItems[0].label}`,
                                                        label: (tooltipItem) =>
                                                            `${tooltipItem.dataset.label}: ${formatCurrencyDollars(tooltipItem.raw)}`,
                                                    },
                                                },
                                                legend: {
                                                    position: 'top',
                                                },
                                            },
                                            scales: {
                                                x: commonAxesStyles_x,
                                                y: commonAxesStyles_y,
                                            },
                                        }}
                                    />
                                </div>
                                <div className="widget-text">
                                    Last year you {mostRecentReturn_state >= 0 ? 'got a net refund' : 'made a net payment'} of{' '}
                                    <b style={{ color: '#6a84a6' }}>{formatCurrencyDecimal(mostRecentReturn_state)}</b> for your state taxes.
                                </div>
                            </div>
                        </div>



                        </>
                )}
                {currentView === 'FeatureRequest' && <FeatureRequest />}
                {currentView === 'BugReport' && <BugReport />}
                {availableYears.includes(currentView) && (
                    <TaxYearView
                        data={data.find((item) => item.taxYear === currentView) || {}}
                        
                        allData={data}

                    />

                )}
            </div>
           
        </div>
    );
}


export default Dashboard;
